import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  type CreateCustomPropertyDefinition,
  type CustomPropertyDefinitionResponse,
  type UpdateCustomPropertyDefinition,
} from '@zag/core/models';
import { type Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CustomPropertyDefinitionsApiService {
  private readonly http = inject(HttpClient);
  private readonly basePath = '/api/v1/core/custom-property-definitions';

  private readonly refreshTrigger = signal(0);

  /**
   * Shared list resource for the settings catalog.
   * Reloads when `reload()` is called (e.g. after save/delete from the edit page).
   */
  readonly listResource = rxResource<CustomPropertyDefinitionResponse[], { _t: number }>({
    params: () => ({ _t: this.refreshTrigger() }),
    stream: () => this.getAll(),
  });

  reload(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  getByEntityType(entityType: string): Observable<CustomPropertyDefinitionResponse[]> {
    return this.http.get<CustomPropertyDefinitionResponse[]>(this.basePath, {
      params: { entityType },
    });
  }

  getAll(): Observable<CustomPropertyDefinitionResponse[]> {
    return this.http.get<CustomPropertyDefinitionResponse[]>(this.basePath);
  }

  getOne(id: string): Observable<CustomPropertyDefinitionResponse> {
    return this.http.get<CustomPropertyDefinitionResponse>(`${this.basePath}/${id}?include=translations`);
  }

  create(data: CreateCustomPropertyDefinition): Observable<CustomPropertyDefinitionResponse> {
    return this.http.post<CustomPropertyDefinitionResponse>(this.basePath, data);
  }

  update(id: string, data: UpdateCustomPropertyDefinition): Observable<CustomPropertyDefinitionResponse> {
    return this.http.patch<CustomPropertyDefinitionResponse>(`${this.basePath}/${id}`, data);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/${id}`);
  }
}
